import './ScoreReport.css';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { navigationBarState } from '../../../states/NavigationBar';
import axios from 'axios';
import toast from 'react-hot-toast';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

function ScoreReport() {
    const { testId } = useParams();
    const navigate = useNavigate();
    const setNavigationBarState = useSetRecoilState(navigationBarState);
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        setNavigationBarState({
            title: '성적표',
            'left-item': { type: 'back' },
        });
        fetchScoreReport();
    }, [testId]);

    const fetchScoreReport = async () => {
        try {
            const response = await axios.get(`/tests/${testId}/score-report`);
            if (response.data.success) {
                setReportData(response.data);
            } else {
                toast.error(response.data.message || '성적표를 불러올 수 없습니다.');
                navigate(-1);
            }
        } catch (error) {
            const message = error.response?.data?.message || '성적표를 불러오는데 실패했습니다.';
            toast.error(message);
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = useCallback(() => {
        if (isDownloading || !reportData) return;

        setIsDownloading(true);

        const pdfUrl = reportData.pdf_url;

        // S3 URL인 경우 (https로 시작) 직접 다운로드
        if (pdfUrl && pdfUrl.startsWith('https://')) {
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = `성적표_${reportData?.test_info?.title || 'score_report'}.pdf`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('PDF 다운로드 완료');
            setIsDownloading(false);
        } else {
            // API 엔드포인트인 경우 blob으로 다운로드
            axios.get(pdfUrl || `/tests/${testId}/score-report/pdf`, { responseType: 'blob' })
                .then((response) => {
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `성적표_${reportData?.test_info?.title || 'score_report'}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    toast.success('PDF 다운로드 완료');
                })
                .catch((error) => {
                    console.error('PDF 다운로드 오류:', error);
                    toast.error('PDF 다운로드에 실패했습니다.');
                })
                .finally(() => {
                    setIsDownloading(false);
                });
        }
    }, [isDownloading, testId, reportData]);

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleIssueCertificate = () => {
        // TODO: 인증서 발급 페이지로 이동
        toast('인증서 발급 기능은 준비 중입니다.');
    };

    if (loading) {
        return (
            <div className="score-report-page">
                <div className="score-report-loading">
                    <div className="score-report-loading-spinner"></div>
                    <div className="score-report-loading-text">성적표를 불러오는 중...</div>
                </div>
            </div>
        );
    }

    if (!reportData) {
        return null;
    }

    const { test_info, total_score, can_issue_certificate } = reportData;

    return (
        <div className="score-report-page">
            <div className="score-report-container">
                {/* 점수 결과 카드 */}
                <div className="score-report-result-card">
                    <div className="score-report-result-header">
                        <span className="score-report-result-title">성적 결과</span>
                        <span className="score-report-result-test-name">{test_info.title}</span>
                    </div>
                    <div className="score-report-result-score">
                        <span className="score-report-result-emoji">📊</span>
                        <div className="score-report-result-info">
                            <div className="score-report-result-score-value">
                                <span className="score-report-result-score-number">{total_score}</span>
                                <span className="score-report-result-score-max">/ 100점</span>
                            </div>
                            <span className="score-report-result-level">
                                {test_info.test_level}
                            </span>
                        </div>
                    </div>
                </div>

                {/* PDF 성적표 프리뷰 */}
                <div className="score-report-pdf-section">
                    <div className="score-report-pdf-preview">
                        <DescriptionOutlinedIcon className="score-report-pdf-icon" />
                        <div className="score-report-pdf-info">
                            <span className="score-report-pdf-title">성적표 (PDF)</span>
                            <span className="score-report-pdf-desc">상세 성적 정보가 포함된 PDF 문서</span>
                        </div>
                    </div>
                    <button
                        className="score-report-download-btn"
                        onClick={handleDownloadPDF}
                        disabled={isDownloading}
                    >
                        <FileDownloadOutlinedIcon />
                        <span>{isDownloading ? '다운로드 중...' : '성적표 다운로드'}</span>
                    </button>
                </div>

                {/* 하단 버튼 영역 */}
                <div className="score-report-buttons">
                    <button
                        className="score-report-back-btn"
                        onClick={handleGoBack}
                    >
                        뒤로가기
                    </button>
                    {can_issue_certificate && (
                        <button
                            className="score-report-certificate-btn"
                            onClick={handleIssueCertificate}
                        >
                            인증서 발급
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ScoreReport;
